import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row, Select, App, Typography } from 'antd';
import { getNotificationById, createNotification, updateNotification, listRoles, listNotificationsOptions, listModules} from '../../services/notifications';
import { Switch } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;

const { useApp } = App;


const NotificationUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const navigate = useNavigate();
  const { message } = useApp();
  const [form] = Form.useForm();
  const { can } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    listRoles()
      .then((rolesList) => {
        setRoles(rolesList);
      })
      .catch(() => {
        message.error('No se pudieron cargar los roles');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listNotificationsOptions()
      .then((notificationsList) => {
        setNotifications(notificationsList);
      })
      .catch(() => {
        message.error('No se pudieron cargar las notificaciones');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listModules()
      .then((modulesList) => {
        setModules(modulesList);
      })
      .catch(() => {
        message.error('No se pudieron cargar los modulos');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getNotificationById(id)
      .then((notification) => {
        form.setFieldsValue({
          email: notification.email
            ? notification.email
                .split(',')
                .map((e) => e.trim())
                .filter(Boolean)
            : [],
          notification_id: Number(notification.notification?.id),
          module_id: Number(notification.module?.id),
          roles: notification.notificationDetailRoles
            ? notification.notificationDetailRoles.map((r) => r.role.id)
            : [], 
          hidden_copy: notification.hidden_copy,
        });
      })
      .catch(() => {
        message.error('No se pudo cargar la notificación');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, form]);

  /**
   * Función que se ejecuta cuando se envía el formulario
   */
  const onSubmit = async (values) => {
    try {
      setLoading(true);

      if (isEdit && id) {
        const updateData = {
          email: values.email && values.email.length > 0
            ? values.email
                .map((e) => e.trim().toLowerCase())
                .filter(Boolean)
                .join(',')
            : null,
          notification_id: values.notification_id,
          module_id: values.module_id,
          hidden_copy: values.hidden_copy,
          roleIds: values.roles,
        };


        await updateNotification(id, updateData);
        message.success('Notificación actualizada correctamente');
      } else {
        const createData = {
          email: values.email && values.email.length > 0
            ? values.email
                .map((e) => e.trim().toLowerCase())
                .filter(Boolean)
                .join(',')
            : null,
          notification_id: values.notification_id,
          module_id: values.module_id,
          hidden_copy: values.hidden_copy,
          roleIds: values.roles,
        };

        await createNotification(createData);
        message.success('Notificación agregada.');
      }

      navigate('/notifications/all');
    } catch (err) {
      // Extraer datos del error
      const errorData = err?.response?.data || err?.data || {};
      const errorObj = errorData?.error || {};
      const status = errorObj?.status || err?.response?.status || 500;
      const errorCode = errorObj?.code;
      const errorMessage = errorObj?.message || errorData?.message || err?.message || 'Error al guardar el usuario';
      
      // Si es un error de validación (422), mostrar errores en los campos del formulario
      if (status === 422 && (errorCode === 'VALIDATION_ERROR' || errorObj?.details?.issues)) {
        const issues = errorObj?.details?.issues || errorObj?.issues || [];
        
        console.log('Issues encontrados:', issues);
        
        if (issues.length > 0) {
          // Limpiar errores previos
          form.setFields([]);
          
          const fieldErrors = issues
            .filter((issue) => issue.path && issue.message) 
            .map((issue) => {
              let fieldPath = issue.path;
              
              if (typeof fieldPath === 'string') {
                fieldPath = [fieldPath];
              } else if (!Array.isArray(fieldPath)) {
                fieldPath = [String(fieldPath)];
              }
              
              console.log('Mapeando error para campo:', fieldPath, 'Mensaje:', issue.message);
              
              return {
                name: fieldPath,
                errors: [issue.message || 'Error de validación'],
              };
            });
          
          console.log('Field errors a establecer:', fieldErrors);
          
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            console.log('Errores establecidos en el formulario');
          }
          
          message.error(errorMessage || 'Por favor, corrige los errores en el formulario');
        } else {
          message.error(errorMessage);
        }
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/notifications/all');
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button 
            type="text" 
            icon={<LeftOutlined style={{ fontSize: '24px', strokeWidth: 4 }} />} 
            style={{ 
              padding: 0,
              color: '#BA955E',
              fontSize: '24px',
              fontWeight: '700',
              height: 'auto'
            }}
            onClick={handleBack}
          />
          <Title level={3} style={{ marginBottom: 0 }}>{isEdit ? 'Editar notificación' : 'Agregar notificación'}</Title>
      </div>
      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Notificación"
                name="notification_id"
                rules={[{ required: true, message: 'Debes seleccionar una notificación' }]}
              >
                <Select
                  placeholder="Selecciona una notificación"
                  options={notifications.map((not) => ({
                    value: Number(not.id),
                    label: not.description,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Módulo"
                name="module_id"
                rules={[{ required: true, message: 'Debes seleccionar un módulo' }]}
              >
                <Select
                  placeholder="Selecciona un módulo"
                  options={modules.map((module) => ({
                    value: Number(module.id),
                    label: module.description,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Perfil"
                name="roles"
                rules={[{ required: true, message: 'Debes seleccionar un perfil' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Selecciona un perfil"
                  options={roles.map((role) => ({
                    value: role.id,
                    label: role.name,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Correos electrónicos"
                name="email"
                rules={[
                  {
                    required: !isEdit,
                    message: 'Debes agregar al menos un correo',
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.length === 0) return Promise.resolve();

                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                      const invalidEmails = value.filter(
                        (email) => !emailRegex.test(email),
                      );

                      if (invalidEmails.length > 0) {
                        return Promise.reject(
                          new Error(`Correo(s) inválido(s): ${invalidEmails.join(', ')}`),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  mode="tags"
                  placeholder="Escribe un correo y presiona Enter"
                  tokenSeparators={[',', ' ']}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={10} justify="end">
            <Col>
                <Form.Item
                label="¿Incluir como copia oculta?"
                name="hidden_copy"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate(-1)} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            {can('notifications.edit') && (<Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEdit ? 'Guardar cambios' : 'Guardar'}
              </Button>
            </Col>)}
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default NotificationUpsertPage;

